import { useState } from "react";
import { TableRow, TableCell } from "@/components/ui/table";
import { Pencil, ListTree, Trash2, ImageOff } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ActionButton } from "@/components/ui/action-button";
import { ImagePreview } from "@/components/ui/image-preview";
import { StatusBadge } from "@/components/events/StatusBadge";
import { Spinner } from "@/components/ui/spinner";
import { useDeleteEvent, useUpdateEventStatus } from "@/hooks/useEvents";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { EventResponse, EventStatus } from "@/types";
import { EditParentEventDialog } from "./EditParentEventDialog";
import { ManageChildrenDialog } from "./ManageChildrenDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function formatDate(dateStr: string) {
  return new Intl.DateTimeFormat("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(dateStr));
}

function formatDateRange(start: string, end: string | null) {
  const s = formatDate(start);
  return end ? `${s} – ${formatDate(end)}` : s;
}

interface ParentEventRowProps {
  event: EventResponse;
}

export function ParentEventRow({ event }: ParentEventRowProps) {
  const deleteEvent = useDeleteEvent();
  const updateStatus = useUpdateEventStatus();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [childrenOpen, setChildrenOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteEvent.mutateAsync(event.id);
      void queryClient.invalidateQueries({ queryKey: ["parent-events"] });
      toast.success("Wydarzenie usunięte");
    } catch (err) {
      toast.error("Nie udało się usunąć wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleStatusChange = async (status: string) => {
    try {
      await updateStatus.mutateAsync({ id: event.id, status: status as EventStatus });
      void queryClient.invalidateQueries({ queryKey: ["parent-events"] });
      toast.success("Status zaktualizowany");
    } catch (err) {
      toast.error("Nie udało się zmienić statusu", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <>
      <TableRow>
        <TableCell className="w-[70px] min-w-[70px]">
          {event.coverImage ? (
            <ImagePreview src={event.coverImage.thumb} alt={event.title} />
          ) : (
            <div className="w-[70px] h-[70px] bg-slate-100 rounded flex items-center justify-center">
              <ImageOff className="h-5 w-5 text-slate-400" />
            </div>
          )}
        </TableCell>
        <TableCell className="max-w-xs">
          <div className="flex items-center gap-2">
            <p className="font-medium truncate">{event.title}</p>
            {event.isPreview && (
              <span className="inline-flex shrink-0 items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                Podgląd
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate">{event.id}</p>
        </TableCell>
        <TableCell className="whitespace-nowrap">
          {formatDateRange(event.date, event.endDate)}
        </TableCell>
        <TableCell>
          <Select
            value={event.status}
            onValueChange={handleStatusChange}
            disabled={updateStatus.isPending}
          >
            <SelectTrigger className="w-36 h-8">
              <SelectValue>
                <StatusBadge status={event.status} rejectionReason={event.rejectionReason} />
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Oczekujące</SelectItem>
              <SelectItem value="approved">Zatwierdzone</SelectItem>
              <SelectItem value="rejected">Odrzucone</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <ActionButton tooltip="Program" onClick={() => setChildrenOpen(true)}>
              <ListTree className="h-3.5 w-3.5" />
            </ActionButton>
            <ActionButton tooltip="Edytuj" onClick={() => setEditOpen(true)}>
              <Pencil className="h-3.5 w-3.5" />
            </ActionButton>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <ActionButton tooltip="Usuń" className="text-destructive">
                  {deleteEvent.isPending ? (
                    <Spinner className="h-3.5 w-3.5" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                </ActionButton>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Usuń wydarzenie nadrzędne</AlertDialogTitle>
                  <AlertDialogDescription>
                    Czy na pewno chcesz usunąć „{event.title}"? Przypisane wydarzenia zostaną
                    odgrupowane.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Anuluj</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive">
                    Usuń
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TableCell>
      </TableRow>
      <EditParentEventDialog event={event} open={editOpen} onOpenChange={setEditOpen} />
      <ManageChildrenDialog
        parentId={event.id}
        parentTitle={event.title}
        currentChildren={[]}
        open={childrenOpen}
        onOpenChange={setChildrenOpen}
      />
    </>
  );
}
